import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SEGMENTS = ["Swiss Broker", "Real Estate Agent", "Family Office", "Entrepreneur", "Business Owner"];
const STAGES = ["New", "Contacted", "Qualified", "Meeting", "Proposal", "Won", "Lost"];

const TARGET_FIELDS = [
  { key: "name", label: "Name*", required: true },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "company", label: "Company" },
  { key: "segment", label: "Segment*", required: true },
  { key: "location", label: "Location" },
  { key: "budget_sgd", label: "Budget SGD" },
  { key: "net_worth_range", label: "Net worth range" },
  { key: "stage", label: "Stage" },
  { key: "notes", label: "Notes" },
];

// Very small CSV parser (handles quoted fields, commas inside quotes, escaped quotes)
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let i = 0;
  let inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQuotes = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { cur.push(field); field = ""; i++; continue; }
    if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      cur.push(field); rows.push(cur); cur = []; field = ""; i++; continue;
    }
    field += c; i++;
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((v) => v && v.trim() !== ""));
}

function autoMap(headers) {
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const map = {};
  const aliases = {
    name: ["name", "fullname", "contact", "contactname", "leadname"],
    email: ["email", "emailaddress", "mail"],
    phone: ["phone", "telephone", "mobile", "tel"],
    company: ["company", "firm", "organisation", "organization", "employer", "companyname"],
    segment: ["segment", "type", "category", "leadtype"],
    location: ["location", "city", "canton", "country", "region"],
    budget_sgd: ["budget", "budgetsgd", "budgets$", "sgdbudget", "budgetsingapore"],
    net_worth_range: ["networth", "networthrange", "aum", "wealth"],
    stage: ["stage", "status", "pipelinestage"],
    notes: ["notes", "note", "comment", "comments", "remarks"],
  };
  TARGET_FIELDS.forEach((f) => {
    const hits = aliases[f.key] || [f.key];
    const idx = headers.findIndex((h) => hits.includes(norm(h)));
    map[f.key] = idx >= 0 ? String(idx) : "none";
  });
  return map;
}

function coerceSegment(v) {
  if (!v) return null;
  const s = String(v).trim().toLowerCase();
  const found = SEGMENTS.find((x) => x.toLowerCase() === s);
  if (found) return found;
  if (s.includes("broker")) return "Swiss Broker";
  if (s.includes("agent")) return "Real Estate Agent";
  if (s.includes("family")) return "Family Office";
  if (s.includes("entrepren")) return "Entrepreneur";
  if (s.includes("business") || s.includes("owner")) return "Business Owner";
  return null;
}

export default function CSVImportDialog({ open, onClose, onImported }) {
  const [step, setStep] = useState(1); // 1=upload, 2=map, 3=preview
  const [rawText, setRawText] = useState("");
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [skipDup, setSkipDup] = useState(true);

  const headers = rows[0] || [];
  const dataRows = useMemo(() => rows.slice(1), [rows]);

  const reset = () => { setStep(1); setRawText(""); setRows([]); setMapping({}); setPreview(null); };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setRawText(text);
      const parsed = parseCSV(text);
      if (parsed.length < 2) { toast.error("CSV needs a header row + at least one data row"); return; }
      setRows(parsed);
      setMapping(autoMap(parsed[0]));
      setStep(2);
    };
    reader.readAsText(file);
  };

  const buildPayload = () => dataRows.map((row) => {
    const get = (key) => {
      const idx = mapping[key];
      if (idx === "none" || idx === undefined) return null;
      const v = row[Number(idx)];
      return v === undefined || v === "" ? null : v;
    };
    const budget = get("budget_sgd");
    return {
      name: (get("name") || "").trim(),
      email: get("email"),
      phone: get("phone"),
      company: get("company"),
      segment: coerceSegment(get("segment")) || "Entrepreneur",
      location: get("location") || "Switzerland",
      budget_sgd: budget ? Number(String(budget).replace(/[^0-9.]/g, "")) || null : null,
      net_worth_range: get("net_worth_range"),
      stage: (() => { const s = get("stage"); return STAGES.includes(s) ? s : "New"; })(),
      notes: get("notes") || "",
      source: "CSV Import",
    };
  }).filter((r) => r.name);

  const runPreview = async () => {
    const payload = buildPayload();
    if (payload.length === 0) { toast.error("No valid rows (Name column empty)"); return; }
    try {
      const res = await api.post("/leads/import/preview", { rows: payload, skip_duplicates: skipDup });
      setPreview(res.data);
      setStep(3);
    } catch (_e) { toast.error("Preview failed"); }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const payload = buildPayload();
      const res = await api.post("/leads/import", { rows: payload, skip_duplicates: skipDup });
      toast.success(`Imported ${res.data.inserted}, skipped ${res.data.skipped}`);
      onImported?.();
      onClose();
      reset();
    } catch (_e) { toast.error("Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent data-testid="csv-import-dialog" className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#C5A880]" /> Import leads from CSV
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="py-6">
            <label htmlFor="csv-file" className="block cursor-pointer border-2 border-dashed border-[#E2E8F0] rounded-lg p-10 text-center hover:border-[#C5A880] hover:bg-[#FDF8EF]/40 transition-colors">
              <FileText className="w-8 h-8 text-[#C5A880] mx-auto mb-3" />
              <div className="font-medium text-[#0A1128]">Choose a CSV file</div>
              <div className="text-xs text-slate-500 mt-1">First row must be column headers. UTF-8 encoded.</div>
              <input data-testid="csv-file-input" id="csv-file" type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
            </label>
            <div className="mt-4 text-xs text-slate-500">
              Supported columns (aliases auto-detected): name, email, phone, company, segment, location, budget_sgd, net_worth_range, stage, notes.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="py-2 max-h-[60vh] overflow-y-auto">
            <div className="label-eyebrow mb-2">Map your columns</div>
            <p className="text-sm text-slate-600 mb-4">We tried to auto-detect. Adjust below — segment and name are required.</p>
            <div className="grid grid-cols-2 gap-3">
              {TARGET_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Select value={mapping[f.key] || "none"} onValueChange={(v) => setMapping({ ...mapping, [f.key]: v })}>
                    <SelectTrigger data-testid={`csv-map-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— none —</SelectItem>
                      {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <input type="checkbox" id="skip-dup" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} />
              <label htmlFor="skip-dup" className="text-slate-700">Skip rows where email already exists in CRM</label>
            </div>
          </div>
        )}

        {step === 3 && preview && (
          <div className="py-2 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center gap-6 mb-4">
              <div><div className="label-eyebrow">Ready to insert</div><div className="font-serif-display text-2xl text-[#0A1128]">{preview.total - preview.duplicates}</div></div>
              <div><div className="label-eyebrow">Duplicates</div><div className="font-serif-display text-2xl text-[#C5A880]">{preview.duplicates}</div></div>
              <div><div className="label-eyebrow">Total in file</div><div className="font-serif-display text-2xl text-slate-500">{preview.total}</div></div>
            </div>
            <div className="border border-[#E2E8F0] rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                    <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Name</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Email</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Company</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Segment</TableHead>
                    <TableHead className="text-[11px] uppercase tracking-widest text-slate-500">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 30).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{r.email || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-600">{r.company || "—"}</TableCell>
                      <TableCell>{r.segment_valid ? <Badge variant="secondary" className="rounded-full">{r.segment}</Badge> : <Badge className="bg-red-50 text-red-700 rounded-full">Invalid</Badge>}</TableCell>
                      <TableCell>{r.duplicate
                        ? <span className="text-xs text-[#C5A880] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Duplicate</span>
                        : <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> New</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.rows.length > 30 && <div className="p-3 text-xs text-slate-500 text-center">…and {preview.rows.length - 30} more</div>}
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>}
          <Button variant="ghost" onClick={() => { onClose(); reset(); }}>Cancel</Button>
          {step === 2 && <Button data-testid="csv-preview-btn" onClick={runPreview} className="bg-[#0A1128] text-white hover:bg-[#152243]">Preview</Button>}
          {step === 3 && (
            <Button data-testid="csv-import-btn" disabled={importing} onClick={runImport} className="bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E]">
              {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</> : <>Import {preview?.total - preview?.duplicates} leads</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
